import { useCallback, useState, useRef } from 'react';
import { z, ZodEffects, ZodIssue, ZodObject, ZodRawShape } from 'zod';
import { isObjectEmpty } from '@utils/isObjectEmpty';
import {
  Errors,
  ExternalField,
  FieldValue,
  ValidateField,
  ValidationResponse,
  ValidationSchema,
  ValidationStatus,
} from '@interfaces/externalValidationResolver';

const validateField = async ({
  isPrevalidationValid,
  externalField,
  fieldValue,
  setIsValid,
  setIsLoading,
  setValidationResponse,
  externalErrorsRef,
  isValidRef,
}: ValidateField): Promise<void> => {
  const fieldName = externalField.name;
  const validate = externalField.validate;

  let newIsValid = isValidRef.current;
  setIsValid((isValid) => {
    newIsValid = { ...isValid, [fieldName]: false };
    return newIsValid;
  });
  isValidRef.current = newIsValid;

  if (isPrevalidationValid) {
    setIsLoading((isLoading) => ({
      ...isLoading,
      [fieldName]: true,
    }));

    const validationResponse = await validate(fieldValue);
    const { success, errorMessage } = validationResponse;

    setValidationResponse((vr) => ({
      ...vr,
      [fieldName]: validationResponse,
    }));

    setIsLoading((isLoading) => ({
      ...isLoading,
      [fieldName]: false,
    }));

    if (!success) {
      const error = {
        path: [fieldName],
        message: errorMessage,
        code: 'custom',
      } as ZodIssue;

      externalErrorsRef.current = {
        ...externalErrorsRef.current,
        [fieldName]: error,
      };
      throw new z.ZodError([error]);
    }

    delete externalErrorsRef.current[fieldName];
    setIsValid((isValid) => {
      newIsValid = { ...isValid, [fieldName]: true };
      return newIsValid;
    });
    isValidRef.current = newIsValid;
  }
};

const whichPropertyChanged = <T extends Record<string, any>>(
  oldObj: T,
  newObj: T,
): string => {
  if (isObjectEmpty(oldObj)) {
    const firstNonEmptyKey = Object.entries(newObj).find(
      (entry) => entry[1],
    )?.[0];
    return firstNonEmptyKey || '';
  }

  const entry = Object.entries(oldObj).find((entry) => {
    return entry[1] !== newObj[entry[0]];
  });

  return entry?.[0] || '';
};

const generateErrorsObject = (errors: z.ZodError) =>
  errors.issues?.reduce(
    (acc, err) => ({
      ...acc,
      [err.path[0]]: { message: err.message },
    }),
    {},
  );

export const useExternalValidationResolver = <T extends ZodRawShape>(
  validationSchema: ValidationSchema<T>,
  externalFields: ExternalField[],
) => {
  type ValidationSchema = z.infer<typeof validationSchema>;

  const resolvePrevalidation = (fieldName: string, fieldValue: FieldValue) => {
    if (validationSchema instanceof ZodObject)
      return validationSchema.shape[fieldName].safeParse(fieldValue).success;

    if (validationSchema instanceof ZodEffects)
      return validationSchema._def.schema.shape[fieldName].safeParse(fieldValue)
        .success;

    return false;
  };

  const validationStatusInitialState = externalFields.reduce(
    (acc, field) => ({
      ...acc,
      [field.name]: false,
    }),
    {},
  );

  const validationResponseInitialState = externalFields.reduce(
    (acc, field) => ({
      ...acc,
      [field.name]: null,
    }),
    {},
  );

  const [dataState, setDataState] = useState<ValidationSchema>(
    {} as ValidationSchema,
  );
  const [isLoading, setIsLoading] = useState<ValidationStatus>(
    validationStatusInitialState,
  );
  const [isValid, setIsValid] = useState<ValidationStatus>(
    validationStatusInitialState,
  );
  const [validationResponse, setValidationResponse] =
    useState<ValidationResponse>(validationResponseInitialState);
  const debounceRef = useRef<NodeJS.Timeout>();
  const isValidRef = useRef<ValidationStatus>(validationStatusInitialState);
  const externalErrorsRef = useRef<Errors>({});

  const externalValidationResolver = useCallback(
    async (data: ValidationSchema) => {
      setDataState({ ...data });

      const generateReturnValue = () => {
        const parsedData = validationSchema.parse(data);

        const hasExternalErrors = Object.entries(isValidRef.current).some(
          (entry) => !entry[1],
        );
        const errorsObject = hasExternalErrors ? externalErrorsRef.current : {};

        return {
          values: parsedData,
          errors: errorsObject,
        };
      };

      try {
        // Dispara validação de campos externos caso form já venha preenchido com valores válidos
        const hasDefaultValues =
          validationSchema.safeParse(data).success && isObjectEmpty(dataState);

        if (hasDefaultValues) {
          const returnedPromises = await Promise.allSettled(
            externalFields.map(async (externalField) => {
              const fieldName = externalField.name;
              const fieldValue = data[fieldName];

              const isPrevalidationValid = resolvePrevalidation(
                fieldName,
                fieldValue,
              );

              return validateField({
                isPrevalidationValid,
                externalField,
                fieldValue,
                setIsValid,
                setIsLoading,
                setValidationResponse,
                externalErrorsRef,
                isValidRef,
              });
            }),
          );

          const rawIssues = returnedPromises.map(
            (promise) =>
              promise.status === 'rejected' && promise.reason.issues[0],
          );
          const issues = rawIssues.filter((issue) => issue);
          if (issues.length) throw new z.ZodError(issues);

          return generateReturnValue();
        }

        // Valida campo externo caso ele tenha sido alterado
        const changedField = whichPropertyChanged<ValidationSchema>(
          dataState,
          data,
        );

        const changedExternalField = externalFields.find(
          (field) => field.name === changedField,
        );

        if (changedExternalField) {
          const fieldName = changedExternalField.name;
          const fieldValue = data[fieldName];

          const isPrevalidationValid = resolvePrevalidation(
            fieldName,
            fieldValue,
          );

          debounceRef.current && clearTimeout(debounceRef.current);
          const debouncedValidationPromise = new Promise((resolve, reject) => {
            debounceRef.current = setTimeout(async () => {
              validateField({
                isPrevalidationValid,
                externalField: changedExternalField,
                fieldValue,
                setIsValid,
                setIsLoading,
                setValidationResponse,
                externalErrorsRef,
                isValidRef,
              })
                .then(resolve)
                .catch(reject);
            }, 500);
          });

          await debouncedValidationPromise;
        }

        return generateReturnValue();
      } catch (err) {
        // Remove erros internos de externalErrorsRef
        // Corrige um comportamento estranho de erros internos serem injetados no externalErrorsRef
        Object.keys(externalErrorsRef.current).forEach((errorKey) => {
          !externalFields
            .map((externalField) => externalField.name)
            .includes(errorKey) && delete externalErrorsRef.current[errorKey];
        });

        const errorsObject =
          err instanceof z.ZodError
            ? { ...generateErrorsObject(err), ...externalErrorsRef.current }
            : {};

        return {
          values: data,
          errors: errorsObject,
        };
      }
    },
    [dataState],
  );

  return { externalValidationResolver, isLoading, isValid, validationResponse };
};
